const categories = [
  {
    id: 'foods',
    name: 'Élelmiszer',
    description: 'Italok és ételek egy helyen',
    color: '#0F3D2C',
    accent: '#154532',
  },
  {
    id: 'household',
    name: 'Háztartás',
    description: 'Fenntartható tisztítás',
    color: '#0F3D2C',
    accent: '#154532',
  },
  {
    id: 'bodycare',
    name: 'Testápolás',
    description: 'Természetes gondoskodás',
    color: '#0F3D2C',
    accent: '#154532',
  },
];

const normalizeProductCategory = (category) => (category === 'drinks' ? 'foods' : category);

const categoryLabels = {
  foods: 'Élelmiszer',
  household: 'Háztartás',
  bodycare: 'Testápolás',
};

const subcategoryOptionsByCategory = {
  foods: [
    { id: 'food', name: 'Ételek' },
    { id: 'drink', name: 'Italok' },
    { id: 'loose-tea', name: 'Szálas tea' },
  ],
  household: [
    { id: 'general', name: 'Általános' },
    { id: 'dishwashing', name: 'Mosogatás' },
    { id: 'laundry', name: 'Mosás' },
  ],
  bodycare: [
    { id: 'solid-shampoos', name: 'Szilárd samponok' },
    { id: 'soaps', name: 'Szappanok' },
  ],
};

const foodLegacySubcategoryMap = {
  shots: 'drink',
  kombucha: 'drink',
  tea: 'loose-tea',
  granola: 'food',
  seeds: 'food',
  snacks: 'food',
  foods: 'food',
  drinks: 'drink',
  etelek: 'food',
  italok: 'drink',
  'szalas-tea': 'loose-tea',
  'szalas-teak': 'loose-tea',
};

const householdLegacySubcategoryMap = {
  cleaners: 'general',
  tisztitoszerek: 'general',
  altalanos: 'general',
  'dish-sheets': 'dishwashing',
  mosogatolapok: 'dishwashing',
  mosogatas: 'dishwashing',
  'laundry-sheets': 'laundry',
  mosolapok: 'laundry',
  mosas: 'laundry',
};

const toLookupKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');

const DEFAULT_PRODUCT_IMAGE_FOCUS = 50;

const clampProductImageFocus = (value, fallback = DEFAULT_PRODUCT_IMAGE_FOCUS) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  if (parsed < 0) return 0;
  if (parsed > 100) return 100;
  return Math.round(parsed);
};

const normalizeProductImageFocus = (focusX, focusY) => ({
  x: clampProductImageFocus(focusX),
  y: clampProductImageFocus(focusY),
});

const getProductImageObjectPosition = (product) => {
  const { x, y } = normalizeProductImageFocus(product?.imageFocusX, product?.imageFocusY);
  return `${x}% ${y}%`;
};

const normalizeProductSubcategory = (category, subcategory) => {
  const normalizedCategory = normalizeProductCategory(category);
  const raw = String(subcategory || '').trim();
  if (!raw) return '';
  const options = subcategoryOptionsByCategory[normalizedCategory] || [];

  if (normalizedCategory === 'foods') {
    const lookupKey = toLookupKey(raw);
    const mapped = foodLegacySubcategoryMap[lookupKey] || raw;
    const allowed = options.map((option) => option.id);
    return allowed.includes(mapped) ? mapped : '';
  }

  if (normalizedCategory === 'household') {
    const lookupKey = toLookupKey(raw);
    const mapped = householdLegacySubcategoryMap[lookupKey] || raw;
    const allowed = options.map((option) => option.id);
    return allowed.includes(mapped) ? mapped : '';
  }

  if (!options.length) return raw;
  const allowed = options.map((option) => option.id);
  return allowed.includes(raw) ? raw : '';
};

const normalizeSingleLineText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeText = (value) => String(value || '').trim();

const normalizePercentageValue = (value) =>
  normalizeSingleLineText(value).replace(/%/g, '').trim();

const normalizeHexColor = (value, fallback) => {
  const normalized = normalizeSingleLineText(value).toUpperCase();
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : fallback;
};

const normalizeStringList = (value) => {
  const source = Array.isArray(value) ? value : [];
  return source
    .map((item) => normalizeSingleLineText(item))
    .filter(Boolean);
};

const createEmptyFoodDetails = () => ({
  netQuantity: '',
  unitPrice: '',
  origin: '',
  allergens: [],
  dietaryTags: [],
  servingSuggestion: '',
  preparation: '',
  usageIdeas: [],
  ingredientHighlight: '',
  ingredientHighlightLabel: '',
  compositionColor: '#0F3D2C',
  excludedIngredients: [],
  nutritionIntro: '',
  nutritionColor: '#D7B55B',
  recipeIdeas: [],
  benefits: [],
  didYouKnow: '',
  ctaTitle: '',
  ctaText: '',
});

const normalizeFoodRecipeIdeas = (recipeIdeas) => {
  const source = Array.isArray(recipeIdeas) ? recipeIdeas : [];
  return source
    .map((recipe) => ({
      title: normalizeSingleLineText(recipe?.title),
      image: normalizeSingleLineText(recipe?.image),
    }))
    .filter((recipe) => recipe.title || recipe.image)
    .slice(0, 3);
};

const normalizeFoodBenefits = (benefits) => {
  const source = Array.isArray(benefits) ? benefits : [];
  return source
    .map((benefit) => ({
      title: normalizeSingleLineText(benefit?.title),
      description: normalizeText(benefit?.description),
    }))
    .filter((benefit) => benefit.title || benefit.description)
    .slice(0, 4);
};

const normalizeFoodDetails = (details) => {
  const source = details && typeof details === 'object' ? details : {};
  return {
    ...createEmptyFoodDetails(),
    netQuantity: normalizeSingleLineText(source.netQuantity),
    unitPrice: normalizeSingleLineText(source.unitPrice),
    origin: normalizeSingleLineText(source.origin),
    allergens: normalizeStringList(source.allergens),
    dietaryTags: normalizeStringList(source.dietaryTags),
    servingSuggestion: normalizeText(source.servingSuggestion),
    preparation: normalizeText(source.preparation),
    usageIdeas: normalizeStringList(source.usageIdeas),
    ingredientHighlight: normalizeSingleLineText(source.ingredientHighlight),
    ingredientHighlightLabel: normalizeSingleLineText(source.ingredientHighlightLabel),
    compositionColor: normalizeHexColor(source.compositionColor, '#0F3D2C'),
    excludedIngredients: normalizeStringList(source.excludedIngredients),
    nutritionIntro: normalizeText(source.nutritionIntro),
    nutritionColor: normalizeHexColor(source.nutritionColor, '#D7B55B'),
    recipeIdeas: normalizeFoodRecipeIdeas(source.recipeIdeas),
    benefits: normalizeFoodBenefits(source.benefits),
    didYouKnow: normalizeText(source.didYouKnow),
    ctaTitle: normalizeSingleLineText(source.ctaTitle),
    ctaText: normalizeText(source.ctaText),
  };
};

const createEmptyHouseholdDetails = () => ({
  packSize: '',
  scent: '',
  suitableFor: [],
  dosage: '',
  ecoFeatures: [],
  warnings: '',
  waterImpactEnabled: false,
  waterImpactTitle: '',
  waterImpactIntro: '',
  waterImpactColor: '#08769A',
  waterImpactMetrics: [],
  waterImpactCallout: '',
  plasticImpactEnabled: false,
  plasticImpactTitle: '',
  plasticImpactIntro: '',
  plasticImpactColor: '#A95610',
  plasticImpactCallout: '',
  // Legacy fields kept so previously saved products can be migrated without data loss.
  impactTemplate: 'water',
  impactTitle: '',
  impactIntro: '',
  impactMetrics: [],
  impactPercentage: '',
  impactPercentageLabel: '',
  impactFootnote: '',
  impactCallout: '',
  averageWaterPercentage: '',
  productWaterPercentage: '',
  waterComparisonText: '',
  transportCostHighlight: '',
  transportCostText: '',
  plasticAmount: '',
  plasticPercentage: '',
  plasticEquivalent: '',
  plasticImpactText: '',
  compositionTitle: '',
  compositionIntro: '',
  excludedIngredients: [],
  usageTitle: '',
  usageSteps: [],
  benefits: [],
  ctaTitle: '',
  ctaText: '',
});

const normalizeHouseholdTextItems = (items, limit) => {
  const source = Array.isArray(items) ? items : [];
  return source
    .map((item) => ({
      title: normalizeSingleLineText(item?.title),
      description: normalizeText(item?.description),
    }))
    .filter((item) => item.title || item.description)
    .slice(0, limit);
};

const normalizeHouseholdImpactMetrics = (metrics) => {
  const source = Array.isArray(metrics) ? metrics : [];
  return source
    .map((metric) => ({
      label: normalizeSingleLineText(metric?.label),
      value: normalizeSingleLineText(metric?.value),
      barPercentage: normalizeSingleLineText(metric?.barPercentage),
      note: normalizeText(metric?.note),
    }))
    .filter((metric) => metric.label || metric.value || metric.barPercentage || metric.note)
    .slice(0, 4);
};

const normalizeHouseholdDetails = (details) => {
  const source = details && typeof details === 'object' ? details : {};
  const legacyWaterTemplate = source.impactTemplate === 'water';
  const legacyPlasticTemplate = source.impactTemplate === 'plastic';
  const hasLegacySharedImpact = Boolean(
    source.impactTitle
    || source.impactIntro
    || source.impactCallout,
  );
  const hasWaterImpactContent = Boolean(
    source.waterImpactTitle
    || source.waterImpactIntro
    || source.averageWaterPercentage
    || source.productWaterPercentage
    || source.waterComparisonText
    || source.transportCostHighlight
    || source.transportCostText
    || (Array.isArray(source.waterImpactMetrics) && source.waterImpactMetrics.length > 0)
    || source.waterImpactCallout
    || (legacyWaterTemplate && hasLegacySharedImpact)
  );
  const hasPlasticImpactContent = Boolean(
    source.plasticImpactTitle
    || source.plasticImpactIntro
    || source.plasticAmount
    || source.plasticPercentage
    || source.plasticEquivalent
    || source.plasticImpactText
    || (Array.isArray(source.impactMetrics) && source.impactMetrics.length > 0)
    || source.plasticImpactCallout
    || (legacyPlasticTemplate && hasLegacySharedImpact)
  );
  return {
    ...createEmptyHouseholdDetails(),
    packSize: normalizeSingleLineText(source.packSize),
    scent: normalizeSingleLineText(source.scent),
    suitableFor: normalizeStringList(source.suitableFor),
    dosage: normalizeText(source.dosage),
    ecoFeatures: normalizeStringList(source.ecoFeatures),
    warnings: normalizeText(source.warnings),
    waterImpactEnabled: typeof source.waterImpactEnabled === 'boolean'
      ? source.waterImpactEnabled
      : legacyWaterTemplate && hasWaterImpactContent,
    waterImpactTitle: normalizeSingleLineText(
      source.waterImpactTitle || (legacyWaterTemplate ? source.impactTitle : ''),
    ),
    waterImpactIntro: normalizeText(
      source.waterImpactIntro || (legacyWaterTemplate ? source.impactIntro : ''),
    ),
    waterImpactColor: normalizeHexColor(source.waterImpactColor, '#08769A'),
    waterImpactMetrics: normalizeHouseholdImpactMetrics(source.waterImpactMetrics),
    waterImpactCallout: normalizeText(
      source.waterImpactCallout || (legacyWaterTemplate ? source.impactCallout : ''),
    ),
    plasticImpactEnabled: typeof source.plasticImpactEnabled === 'boolean'
      ? source.plasticImpactEnabled
      : legacyPlasticTemplate && hasPlasticImpactContent,
    plasticImpactTitle: normalizeSingleLineText(
      source.plasticImpactTitle || (legacyPlasticTemplate ? source.impactTitle : ''),
    ),
    plasticImpactIntro: normalizeText(
      source.plasticImpactIntro || (legacyPlasticTemplate ? source.impactIntro : ''),
    ),
    plasticImpactColor: normalizeHexColor(source.plasticImpactColor, '#A95610'),
    plasticImpactCallout: normalizeText(
      source.plasticImpactCallout || (legacyPlasticTemplate ? source.impactCallout : ''),
    ),
    impactTemplate: source.impactTemplate === 'water' ? 'water' : 'plastic',
    impactTitle: normalizeSingleLineText(source.impactTitle),
    impactIntro: normalizeText(source.impactIntro),
    impactMetrics: normalizeHouseholdImpactMetrics(source.impactMetrics),
    impactPercentage: normalizeSingleLineText(source.impactPercentage),
    impactPercentageLabel: normalizeSingleLineText(source.impactPercentageLabel),
    impactFootnote: normalizeText(source.impactFootnote),
    impactCallout: normalizeText(source.impactCallout),
    averageWaterPercentage: normalizePercentageValue(source.averageWaterPercentage),
    productWaterPercentage: normalizePercentageValue(source.productWaterPercentage),
    waterComparisonText: normalizeText(source.waterComparisonText),
    transportCostHighlight: normalizeSingleLineText(source.transportCostHighlight),
    transportCostText: normalizeText(source.transportCostText),
    plasticAmount: normalizeSingleLineText(source.plasticAmount),
    plasticPercentage: normalizePercentageValue(
      source.plasticPercentage || source.impactPercentage,
    ),
    plasticEquivalent: normalizeSingleLineText(
      source.plasticEquivalent || source.impactPercentageLabel,
    ),
    plasticImpactText: normalizeText(
      source.plasticImpactText || source.impactFootnote,
    ),
    compositionTitle: normalizeSingleLineText(source.compositionTitle),
    compositionIntro: normalizeText(source.compositionIntro),
    excludedIngredients: normalizeStringList(source.excludedIngredients),
    usageTitle: normalizeSingleLineText(source.usageTitle),
    usageSteps: normalizeHouseholdTextItems(source.usageSteps, 4),
    benefits: normalizeFoodBenefits(source.benefits),
    ctaTitle: normalizeSingleLineText(source.ctaTitle),
    ctaText: normalizeText(source.ctaText),
  };
};

const createEmptyBodycareDetails = () => ({
  netQuantity: '',
  texture: '',
  suitableFor: [],
  skinHairTypes: [],
  application: '',
  activeIngredients: [],
  certifications: [],
  warnings: '',
  benefits: [],
  ctaTitle: '',
  ctaText: '',
});

const normalizeBodycareDetails = (details) => {
  const source = details && typeof details === 'object' ? details : {};
  return {
    ...createEmptyBodycareDetails(),
    netQuantity: normalizeSingleLineText(source.netQuantity),
    texture: normalizeSingleLineText(source.texture),
    suitableFor: normalizeStringList(source.suitableFor),
    skinHairTypes: normalizeStringList(source.skinHairTypes),
    application: normalizeText(source.application),
    activeIngredients: normalizeStringList(source.activeIngredients),
    certifications: normalizeStringList(source.certifications),
    warnings: normalizeText(source.warnings),
    benefits: normalizeFoodBenefits(source.benefits),
    ctaTitle: normalizeSingleLineText(source.ctaTitle),
    ctaText: normalizeText(source.ctaText),
  };
};

const normalizeProductGallery = (galleryImages) => {
  const uniqueImages = new Set();
  normalizeStringList(galleryImages).forEach((image) => uniqueImages.add(image));
  return Array.from(uniqueImages).slice(0, 6);
};

const NUTRITION_FIELDS = [
  { key: 'energyKj', label: 'Energia', unit: 'kJ' },
  { key: 'energyKcal', label: 'Energia', unit: 'kcal' },
  { key: 'fat', label: 'Zsír', unit: 'g' },
  { key: 'saturatedFat', label: 'amelyből telített zsírsavak', unit: 'g', indented: true },
  { key: 'carbohydrate', label: 'Szénhidrát', unit: 'g' },
  { key: 'sugars', label: 'amelyből cukrok', unit: 'g', indented: true },
  { key: 'fiber', label: 'Rost', unit: 'g' },
  { key: 'protein', label: 'Fehérje', unit: 'g' },
  { key: 'salt', label: 'Só', unit: 'g' },
];

const createEmptyNutrition = () => ({
  basis: '100 g',
  energyKj: '',
  energyKcal: '',
  fat: '',
  saturatedFat: '',
  carbohydrate: '',
  sugars: '',
  fiber: '',
  protein: '',
  salt: '',
});

const normalizeNutritionValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').trim();
};

const normalizeNutrition = (nutrition) => {
  const source = nutrition && typeof nutrition === 'object' ? nutrition : {};
  const normalized = createEmptyNutrition();
  normalized.basis = source.basis === '100 ml' ? '100 ml' : '100 g';
  NUTRITION_FIELDS.forEach(({ key }) => {
    normalized[key] = normalizeNutritionValue(source[key]);
  });
  return normalized;
};

const buildShortDescription = (product) => {
  const explicitShort = normalizeSingleLineText(product?.shortDescription || product?.description);
  if (explicitShort) return explicitShort;

  const longDescription = normalizeSingleLineText(product?.longDescription);
  if (!longDescription) return '';

  if (longDescription.length <= 120) return longDescription;
  return `${longDescription.slice(0, 117).trim()}...`;
};

const normalizeProduct = (product) => {
  const { x, y } = normalizeProductImageFocus(product?.imageFocusX, product?.imageFocusY);
  return {
    ...product,
    category: normalizeProductCategory(product.category),
    subcategory: normalizeProductSubcategory(product.category, product.subcategory),
    shortDescription: buildShortDescription(product),
    isHungarian: Boolean(product?.isHungarian),
    nutrition: normalizeNutrition(product?.nutrition),
    galleryImages: normalizeProductGallery(product?.galleryImages),
    foodDetails: normalizeFoodDetails(product?.foodDetails),
    householdDetails: normalizeHouseholdDetails(product?.householdDetails),
    bodycareDetails: normalizeBodycareDetails(product?.bodycareDetails),
    imageFocusX: x,
    imageFocusY: y,
  };
};

const getCategoryLabel = (category) => {
  const normalizedCategory = normalizeProductCategory(category);
  return categoryLabels[normalizedCategory] || normalizedCategory || '';
};

const getSubcategoryLabel = (category, subcategory) => {
  const normalizedCategory = normalizeProductCategory(category);
  const normalizedSubcategory = normalizeProductSubcategory(normalizedCategory, subcategory);
  const options = subcategoryOptionsByCategory[normalizedCategory] || [];
  const matchedOption = options.find((option) => option.id === normalizedSubcategory);

  if (matchedOption) return matchedOption.name;
  if (normalizedSubcategory) return normalizedSubcategory;

  return getCategoryLabel(normalizedCategory);
};

const getProductBadgeLabel = (product) => getSubcategoryLabel(product?.category, product?.subcategory);

const getProductAttributeLabel = (product) => {
  if (!product?.sugarFree) return '';

  const category = normalizeProductCategory(product.category);
  if (category === 'household') return 'Műanyagmentes';
  if (category === 'bodycare') return 'Kézműves';
  return 'Hozzáadott cukor nélkül';
};

const defaultProducts = [
  {
    id: 'ginger-shot-classic',
    name: 'Klasszikus Gyömbér Shot',
    price: 1490,
    shortDescription: 'Kézzel préselt gyömbér, citrom és méz.',
    longDescription:
      'A klasszikus gyömbér shot intenzív frissességgel indítja a napot. Friss gyömbér, citrom és egy csipet méz – tiszta energia minden kortyban.',
    category: 'foods',
    subcategory: 'shots',
    sugarFree: false,
    isBestseller: true,
    image:
      'https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Csípős', 'Citrusos', 'Melegítő'],
    ingredients: ['Gyömbér', 'Citrom', 'Méz'],
    storage: 'Hűtve tárold (2-6°C), felbontás után 3 napon belül fogyaszd.',
  },
  {
    id: 'ginger-shot-mint',
    name: 'Mentás Gyömbér Shot',
    price: 1590,
    shortDescription: 'Hűsítő menta és gyömbér kombinációja.',
    longDescription:
      'Hűsítő menta, friss gyömbér és citrusok elegye. Ideális délutáni felfrissüléshez vagy sport után.',
    category: 'foods',
    subcategory: 'shots',
    sugarFree: true,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1464306076886-da185f6a780d?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Friss', 'Mentás', 'Csípős'],
    ingredients: ['Gyömbér', 'Menta', 'Lime'],
    storage: 'Hűtve tárold (2-6°C), felbontás után 3 napon belül fogyaszd.',
  },
  {
    id: 'turmeric-shot',
    name: 'Kurkuma Shot',
    price: 1690,
    shortDescription: 'Kurkuma, gyömbér és fekete bors.',
    longDescription:
      'Az arany shot kurkumával, gyömbérrel és fekete borssal készül, hogy támogassa a napi egyensúlyt.',
    category: 'foods',
    subcategory: 'shots',
    sugarFree: true,
    isBestseller: true,
    image:
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Földes', 'Meleg', 'Enyhén csípős'],
    ingredients: ['Kurkuma', 'Gyömbér', 'Fekete bors', 'Citrom'],
    storage: 'Hűtve tárold (2-6°C), felbontás után 3 napon belül fogyaszd.',
  },
  {
    id: 'citrus-shot',
    name: 'Citrus Shot',
    price: 1390,
    shortDescription: 'Narancs, grapefruit és gyömbér.',
    longDescription:
      'Élénk citrusos keverék gyömbérrel, amely finoman frissít és felpezsdít.',
    category: 'foods',
    subcategory: 'shots',
    sugarFree: true,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1482049016688-2d3e1b311543?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Citrusos', 'Üde', 'Élénk'],
    ingredients: ['Narancs', 'Grapefruit', 'Gyömbér'],
    storage: 'Hűtve tárold (2-6°C), felbontás után 3 napon belül fogyaszd.',
  },
  {
    id: 'kombucha-ginger-lemon',
    name: 'Gyömbér-Citrom Kombucha',
    price: 2590,
    shortDescription: 'Klasszikus kombucha gyömbérrel és citrommal.',
    longDescription:
      'Friss, pezsgő kombucha gyömbérrel és citrommal. Élő kultúrák, könnyű és harmonikus ízvilág.',
    category: 'foods',
    subcategory: 'kombucha',
    sugarFree: true,
    isBestseller: true,
    image:
      'https://images.unsplash.com/photo-1505252585461-04db1eb84625?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Citrusos', 'Enyhén savanykás', 'Friss'],
    ingredients: ['Zöld tea', 'Nád cukor', 'Gyömbér', 'Citrom'],
    storage: 'Hűtve tárold (2-6°C), felbontás után 5 napon belül fogyaszd.',
  },
  {
    id: 'kombucha-berry',
    name: 'Bogyós Kombucha',
    price: 2690,
    shortDescription: 'Málna, fekete ribizli és hibiszkusz.',
    longDescription:
      'Gyümölcsös, virágos kombucha bogyós gyümölcsökkel és hibiszkusszal. Könnyed, mégis karakteres.',
    category: 'foods',
    subcategory: 'kombucha',
    sugarFree: false,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Gyümölcsös', 'Virágos', 'Enyhén savanykás'],
    ingredients: ['Fekete tea', 'Málna', 'Ribizli', 'Hibiszkusz'],
    storage: 'Hűtve tárold (2-6°C), felbontás után 5 napon belül fogyaszd.',
  },
  {
    id: 'kombucha-herbal',
    name: 'Zöldfűszeres Kombucha',
    price: 2790,
    shortDescription: 'Bazsalikom, citromfű és lime.',
    longDescription:
      'Friss zöldfűszerek és citrusok találkozása. Különleges, mégis könnyen szerethető kombucha.',
    category: 'foods',
    subcategory: 'kombucha',
    sugarFree: true,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Zöld', 'Citrusos', 'Friss'],
    ingredients: ['Zöld tea', 'Bazsalikom', 'Citromfű', 'Lime'],
    storage: 'Hűtve tárold (2-6°C), felbontás után 5 napon belül fogyaszd.',
  },
  {
    id: 'ginger-chai',
    name: 'Gyömbéres Chai',
    price: 2290,
    shortDescription: 'Fűszeres chai gyömbérrel és fahéjjal.',
    longDescription:
      'A fűszeres chai keverék gyömbérrel, fahéjjal és kardamommal melegít fel a hűvösebb napokon.',
    category: 'foods',
    subcategory: 'tea',
    sugarFree: false,
    isBestseller: true,
    image:
      'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Fűszeres', 'Meleg', 'Krémes'],
    ingredients: ['Fekete tea', 'Gyömbér', 'Fahéj', 'Kardamom'],
    storage: 'Hűvös, száraz helyen tárold, felbontás után 30 napon belül fogyaszd.',
  },
  {
    id: 'matcha-oat',
    name: 'Matcha Zab Latte',
    price: 2490,
    shortDescription: 'Krémes matcha zab itallal.',
    longDescription:
      'Finom, selymes matcha ital, zabos jegyekkel. Tiszta energia, kiegyensúlyozott fókusz.',
    category: 'foods',
    subcategory: 'tea',
    sugarFree: true,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1517701604599-bb29b565090c?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Földes', 'Krémes', 'Élénk'],
    ingredients: ['Matcha', 'Zab', 'Vanília'],
    storage: 'Hűvös, száraz helyen tárold, felbontás után 30 napon belül fogyaszd.',
  },
  {
    id: 'lemongrass-tea',
    name: 'Citromfű & Gyömbér Tea',
    price: 1990,
    shortDescription: 'Nyugtató citromfű és gyömbér.',
    longDescription:
      'Tiszta, friss tea citromfűvel és gyömbérrel. Késő estére is tökéletes választás.',
    category: 'foods',
    subcategory: 'tea',
    sugarFree: true,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Fűszeres', 'Citrusos', 'Lágy'],
    ingredients: ['Citromfű', 'Gyömbér', 'Citromhéj'],
    storage: 'Hűvös, száraz helyen tárold, felbontás után 30 napon belül fogyaszd.',
  },
  {
    id: 'granola-seeds',
    name: 'Magvas Granola',
    price: 3190,
    shortDescription: 'Pirított magvak és mézes ropogósság.',
    longDescription:
      'Tápláló granola, amely tele van magvakkal és enyhe mézes édességgel. Joghurt mellé vagy önmagában is kiváló.',
    category: 'foods',
    subcategory: 'granola',
    sugarFree: false,
    isBestseller: true,
    image:
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Ropogós', 'Enyhén édes', 'Diós'],
    ingredients: ['Zab', 'Napraforgómag', 'Tökmag', 'Méz'],
    storage: 'Hűvös, száraz helyen tárold, felbontás után 30 napon belül fogyaszd.',
  },
  {
    id: 'seed-mix',
    name: 'Szupermag Mix',
    price: 2890,
    shortDescription: 'Chia, lenmag és tökmag keverék.',
    longDescription:
      'Könnyen adagolható szupermag keverék, amely remekül illik smoothie-hoz vagy salátához.',
    category: 'foods',
    subcategory: 'seeds',
    sugarFree: true,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Diós', 'Földes', 'Semleges'],
    ingredients: ['Chia', 'Lenmag', 'Tökmag'],
    storage: 'Hűvös, száraz helyen tárold, felbontás után 30 napon belül fogyaszd.',
  },
  {
    id: 'coconut-bites',
    name: 'Kókuszos Falatok',
    price: 3090,
    shortDescription: 'Lágy, kókuszos snack datolyával.',
    longDescription:
      'Édes, mégis természetes energialöket kókuszból és datolyából. Tökéletes útközben is.',
    category: 'foods',
    subcategory: 'snacks',
    sugarFree: false,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1482049016688-2d3e1b311543?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Krémes', 'Kókuszos', 'Édes'],
    ingredients: ['Kókusz', 'Datolya', 'Mandula'],
    storage: 'Hűvös, száraz helyen tárold, felbontás után 14 napon belül fogyaszd.',
  },
  {
    id: 'dish-sheets-citrus',
    name: 'Citrusos Mosogatólapok',
    price: 2490,
    shortDescription: 'Zero waste mosogatólapok friss illattal.',
    longDescription:
      'Biológiailag lebomló mosogatólapok, amelyek gyorsan oldódnak és hatékonyan tisztítanak. Citrusos illat, hulladékmentes megoldás.',
    category: 'household',
    subcategory: 'dishwashing',
    sugarFree: false,
    isBestseller: true,
    image:
      'https://images.unsplash.com/photo-1482049016688-2d3e1b311543?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Citrusos illat', 'Gyors oldódás', 'Zero waste'],
    ingredients: ['Növényi alapú tisztító összetevők'],
    storage: 'Száraz, hűvös helyen tárold, nedvességtől védve.',
  },
  {
    id: 'laundry-sheets-lavender',
    name: 'Levendulás Mosólapok',
    price: 2890,
    shortDescription: 'Kímélő mosólapok érzékeny textíliákhoz.',
    longDescription:
      'Koncentrált mosólapok levendula illattal. Könnyen adagolható, helytakarékos és környezetbarát.',
    category: 'household',
    subcategory: 'laundry',
    sugarFree: false,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Levendulás', 'Kímélő', 'Könnyű adagolás'],
    ingredients: ['Növényi alapú mosóösszetevők'],
    storage: 'Száraz helyen tárold, közvetlen napfénytől védve.',
  },
  {
    id: 'multi-cleaner',
    name: 'Univerzális Tisztítószer',
    price: 2590,
    shortDescription: 'Sokoldalú tisztító spray a mindennapokra.',
    longDescription:
      'Friss, növényi alapú univerzális tisztítószer a konyhába és fürdőbe. Gyors, csíkmentes tisztítás.',
    category: 'household',
    subcategory: 'general',
    sugarFree: false,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1583947582886-f3d29cdd1c2e?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Friss', 'Gyors', 'Csíkmentes'],
    ingredients: ['Növényi tisztító összetevők', 'Illóolajok'],
    storage: 'Szobahőmérsékleten tárold, jól zárva.',
  },
  {
    id: 'solid-shampoo-rosemary',
    name: 'Szilárd Sampon – Rozmaring',
    price: 2790,
    shortDescription: 'Erősítő sampon rozmaring olajjal.',
    longDescription:
      'Gazdag, növényi alapú szilárd sampon rozmaring olajjal. Tisztít és frissít anélkül, hogy szárítana.',
    category: 'bodycare',
    subcategory: 'solid-shampoos',
    sugarFree: false,
    isBestseller: true,
    image:
      'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Frissítő', 'Növényi', 'Erősítő'],
    ingredients: ['Rozmaringolaj', 'Kókuszolaj', 'Shea vaj'],
    storage: 'Használat után szárazon tartsd, szellős helyen.',
  },
  {
    id: 'oat-soap',
    name: 'Kézműves Szappan – Zab & Méz',
    price: 1990,
    shortDescription: 'Kímélő, tápláló szappan érzékeny bőrre.',
    longDescription:
      'Hidegen sajtolt szappan zabkivonattal és mézzel. Finom, krémes hab, természetes ápolás.',
    category: 'bodycare',
    subcategory: 'soaps',
    sugarFree: false,
    isBestseller: false,
    image:
      'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=800&q=80',
    flavorNotes: ['Krémes', 'Lágy', 'Tápláló'],
    ingredients: ['Zabkivonat', 'Méz', 'Olívaolaj'],
    storage: 'Száraz, szellős helyen tárold két használat között.',
  },
].map((product) => ({
  ...normalizeProduct({
    isActive: true,
    ...product,
  }),
}));

const getBestsellers = (products) =>
  products.filter((product) => product.isBestseller && product.isActive !== false).slice(0, 4);

const getProductById = (products, id) =>
  products.find((product) => product.id === id && product.isActive !== false);

const getRelatedProducts = (products, id) => {
  const current = getProductById(products, id);
  if (!current) return [];
  const currentCategory = normalizeProductCategory(current.category);
  return products
    .filter(
      (product) =>
        product.id !== id &&
        normalizeProductCategory(product.category) === currentCategory &&
        product.isActive !== false,
    )
    .slice(0, 4);
};

export {
  defaultProducts,
  categories,
  getBestsellers,
  getProductById,
  getRelatedProducts,
  normalizeProductCategory,
  normalizeProductSubcategory,
  normalizeProduct,
  normalizeProductImageFocus,
  getProductImageObjectPosition,
  subcategoryOptionsByCategory,
  getCategoryLabel,
  getSubcategoryLabel,
  getProductBadgeLabel,
  getProductAttributeLabel,
  NUTRITION_FIELDS,
  createEmptyNutrition,
  normalizeNutrition,
  createEmptyFoodDetails,
  normalizeFoodDetails,
  createEmptyHouseholdDetails,
  normalizeHouseholdDetails,
  createEmptyBodycareDetails,
  normalizeBodycareDetails,
  normalizeProductGallery,
};
